import React, { useEffect, useState } from 'react';
import { TransformWrapper, TransformComponent } from 'react-zoom-pan-pinch';

const MapaRoteamento = React.forwardRef(
  (
    {
      mapData,
      fundoUrl,
      estandesCadastrados,
      tiposEspacoFeira,
      nodes,
      edges,
      onMapClick,
      onNodeClick,
      onStandClick,
      onEdgeClick,
      ferramentaAtiva,
      isEditMode,
      onMouseMove,
      mousePos,
      edgeStartNode,
      selectedNodeIds,
      selectedEdgeIds,
      previewEdges,
      onNodeMouseDown,
      onNodeHover,
      coverageRects,
      regraOrigemIds = [],
      regraWaypointIds = [],
      modoSelecaoRegra = null,
    },
    ref
  ) => {
    const [hoveredNode, setHoveredNode] = useState(null);
    const isRegraOrigem = (nodeId) => Array.isArray(regraOrigemIds) && regraOrigemIds.includes(nodeId);
const isRegraWaypoint = (nodeId) => Array.isArray(regraWaypointIds) && regraWaypointIds.includes(nodeId);

const getEstiloRegraNode = (nodeId) => {
  const origem = isRegraOrigem(nodeId);
  const waypoint = isRegraWaypoint(nodeId);

  if (origem && waypoint) {
    return {
      fill: '#7c3aed',
      stroke: '#ffffff',
      filter: 'drop-shadow(0 0 10px rgba(124,58,237,0.9))',
    };
  }

  if (origem) {
    return {
      fill: '#0d6efd',
      stroke: '#ffffff',
      filter: 'drop-shadow(0 0 10px rgba(13,110,253,0.9))',
    };
  }

  if (waypoint) {
    return {
      fill: '#198754',
      stroke: '#ffffff',
      filter: 'drop-shadow(0 0 10px rgba(25,135,84,0.9))',
    };
  }

  return null;
};

    const handleNodeMouseDown = (e, nodeId) => {
      e.stopPropagation();
      if (onNodeMouseDown) onNodeMouseDown(e, nodeId);
    };

    const handleNodeHover = (e, node, isEntering) => {
      if (isEntering) setHoveredNode(node);
      else setHoveredNode(null);

      if (onNodeHover) onNodeHover(e, node.id, isEntering);
    };

    const obterCorDoTipoEspaco = (tipoId) => {
      const tipo = tiposEspacoFeira?.find((t) => t.tipo_espaco_id === Number(tipoId));
      return tipo?.cor || '#4CAF50';
    };

    const getCorCirculacao = (circ) => {
      switch (circ) {
        case 'externo': return '#ff9800';
        case 'misto': return '#9c27b0';
        default: return '#43eb14ff'; // Verde original para interno
      }
    };

    const renderEstandeText = (nodeEl, estande) => {
      const group = document.createElementNS('http://www.w3.org/2000/svg', 'g');
      group.classList.add('estande-label-group');
      group.style.pointerEvents = 'none';

      const bbox = nodeEl.getBBox();
      const centerX = bbox.x + bbox.width / 2;
      const centerY = bbox.y + bbox.height / 2;

      const baseConfig = {
        x: centerX,
        'text-anchor': 'middle',
        fill: '#302f2f',
        'font-family': 'sans-serif',
        'vector-effect': 'non-scaling-stroke',
      };

      const createText = (y, content, fontSize, bold) => {
        const t = document.createElementNS('http://www.w3.org/2000/svg', 'text');
        Object.entries(baseConfig).forEach(([k, v]) => t.setAttribute(k, v));
        t.setAttribute('y', String(y));
        t.setAttribute('style', `font-size:${fontSize}px !important;${bold ? 'font-weight:bold;' : ''}`);

        if (content && content.length > 20) {
          t.textContent = content.substring(0, 17) + '...';
          t.setAttribute('title', content);
        } else {
          t.textContent = content || '';
        }
        return t;
      };

      const createMultiline = (baseY, text) => {
        const maxChars = 10;
        const lines = [];
        if (!text) return lines;

        if (text.length <= maxChars) {
          lines.push(text);
        } else {
          const middle = Math.floor(text.length / 2);
          let splitPos = text.lastIndexOf(' ', middle);
          splitPos = splitPos > 0 ? splitPos : middle;

          lines.push(text.substring(0, splitPos).trim());
          const remaining = text.substring(splitPos).trim();
          lines.push(remaining.length > maxChars ? remaining.substring(0, maxChars - 3) + '...' : remaining);
        }

        return lines.map((line, i) => createText(baseY + i * 12, line, 9, false));
      };

      let y = centerY - 8;
      group.appendChild(createText(y, (estande?.nome || '').toUpperCase(), 20, true));
      y += 12;

      if (estande?.expositor_id != null) {
        const expositor = estande.expositor;
        const nome = ((expositor?.nome_mapa ?? expositor?.nome) || '').toUpperCase();

        if (nome.length > 20) {
          const multi = createMultiline(y, nome);
          multi.forEach((el) => group.appendChild(el));
          y += multi.length * 12;
        } else if (nome) {
          group.appendChild(createText(y, nome, 12, false));
          y += 12;
        }
      }

      return group;
    };

    useEffect(() => {
      const svgElement = ref?.current;
      if (!svgElement || !mapData?.paths) return;

      const camadaEstandes = svgElement.querySelector('#camada-estandes');
      if (!camadaEstandes) return;

      camadaEstandes.innerHTML = '';

      Object.entries(mapData.paths).forEach(([_, pathData]) => {
        const estande = estandesCadastrados?.find((e) => e.hash_path === pathData.hash);
        const cor = estande ? obterCorDoTipoEspaco(estande.tipo_espaco_id) : '#d2d7d2ff';

        const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
        path.setAttribute('d', pathData.path);
        path.setAttribute('fill', cor);
        path.setAttribute('stroke', '#333');
        path.setAttribute('stroke-width', '1');
        path.style.cursor = 'pointer';
        path.setAttribute('data-hash', pathData.hash);

        path.addEventListener('click', (e) => {
          const bbox = path.getBBox();
          onStandClick?.(e, {
            hash: pathData.hash,
            nome: estande?.nome || 'Estande',
            centerX: bbox.x + bbox.width / 2,
            centerY: bbox.y + bbox.height / 2,
          });
        });

        camadaEstandes.appendChild(path);

        if (estande) {
          const labelGroup = renderEstandeText(path, estande);
          camadaEstandes.appendChild(labelGroup);
        }
      });
    }, [mapData, estandesCadastrados, tiposEspacoFeira, onStandClick, ref]);

    const width = mapData?.width || 1000;
    const height = mapData?.height || 1000;

    const isNodeSelected = (id) => {
        if (Array.isArray(selectedNodeIds)) return selectedNodeIds.includes(id);
        if (selectedNodeIds && typeof selectedNodeIds.has === 'function') return selectedNodeIds.has(id);
        return false;
    };

    const isEdgeSelected = (id) => {
      if (Array.isArray(selectedEdgeIds)) return selectedEdgeIds.includes(id);
      if (selectedEdgeIds && typeof selectedEdgeIds.has === 'function') return selectedEdgeIds.has(id);
      return false;
    };

    const existeSelecao = selectedEdgeIds?.length > 0;

    return (
      <div style={{ position: 'relative' }}>
        <TransformWrapper
          initialScale={1}
          minScale={0.5}
          maxScale={16}
          panning={{ disabled: ferramentaAtiva !== 'pan', velocityDisabled: true }}
          wheel={{ step: 0.1 }}
          doubleClick={{ disabled: true }}
        >
          {({ zoomIn, zoomOut, resetTransform, state }) => (
            <>
              <div
                style={{
                  position: 'absolute',
                  top: 120,
                  left: 10,
                  zIndex: 1000,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 5,
                }}
              >
                <button onClick={() => zoomIn()} style={{ padding: '5px 10px' }}>
                  +
                </button>
                <button onClick={() => resetTransform()} style={{ padding: '5px 10px' }}>
                  ⟲
                </button>
                <button onClick={() => zoomOut()} style={{ padding: '5px 10px' }}>
                  -
                </button>
              </div>

              <TransformComponent wrapperStyle={{ width: '100%', height: 600 }}>
                <svg
                  ref={ref}
                  viewBox={`0 0 ${width} ${height}`}
                  style={{
                    zIndex: 1,
                    cursor: ferramentaAtiva === 'pan' ? 'grab' : 'crosshair',
                    touchAction: 'none',
                  }}
                  onClick={(e) => {
                    if (ferramentaAtiva !== 'pan') onMapClick?.(e);
                  }}
                >
                  <g id="fundo">
                    {fundoUrl ? (
                      <image href={fundoUrl} x="0" y="0" width={width} height={height} style={{ pointerEvents: 'none' }} />
                    ) : null}
                  </g>

                  <g id="camada-estandes" />

<g id="camada-cobertura" style={{ pointerEvents: 'none' }}>
  {Array.isArray(coverageRects) && coverageRects.map(r => (
    <rect
      key={r.key}
      x={r.x}
      y={r.y}
      width={r.w}
      height={r.h}
      fill={r.fill}
      opacity={0.18}
    />
  ))}
</g>

                  <g id="camada-rotas" onMouseMove={onMouseMove}>
                    {isEditMode && ferramentaAtiva === 'addEdge' && edgeStartNode && mousePos ? (
                      (() => {
                        const startNode = nodes?.find((n) => n.id === edgeStartNode);
                        if (!startNode) return null;
                        return (
                          <line
                            x1={startNode.x}
                            y1={startNode.y}
                            x2={mousePos.x}
                            y2={mousePos.y}
                            stroke="rgba(67, 235, 20, 0.5)"
                            strokeWidth={10 / (state?.scale || 1)}
                            strokeDasharray="5,5"
                            style={{ pointerEvents: 'none' }}
                          />
                        );
                      })()
                    ) : null}

                    {Array.isArray(edges) &&
                      edges.map((edge) => {
                        const sourceNode = nodes?.find((n) => n.id === edge.source);
                        const targetNode = nodes?.find((n) => n.id === edge.target);
                        if (!sourceNode || !targetNode) return null;

                        const isSelected = isEdgeSelected(edge.id);
                        const corBase = getCorCirculacao(edge.circulacao);

                        return (
                          <line
                            key={edge.id}
                            onClick={isEditMode ? (e) => onEdgeClick?.(e, edge.id) : undefined}
                            x1={sourceNode.x}
                            y1={sourceNode.y}
                            x2={targetNode.x}
                            y2={targetNode.y}
                            stroke={isSelected ? '#00e5ff' : corBase}
                            strokeWidth={(isSelected ? 28 : 20) / (state?.scale || 1)}
                            strokeDasharray={
                              isSelected
                                ? null
                                : `${4 / (state?.scale || 1)} ${4 / (state?.scale || 1)}`
                            }
                            opacity={isSelected ? 1 : existeSelecao ? 0.15 : 0.85}
                            style={{
                              cursor: isEditMode && ferramentaAtiva === 'delete' ? 'crosshair' : 'default',
                              filter: isSelected
                                ? 'drop-shadow(0 0 10px rgba(0,229,255,0.9))'
                                : 'none',
                              pointerEvents: 'stroke'
                            }}
                          />

                        );
                      })}

                    {Array.isArray(previewEdges) &&
                      previewEdges.map((edge) => {
                        const sourceNode = nodes?.find((n) => n.id === edge.source);
                        const targetNode = nodes?.find((n) => n.id === edge.target);
                        if (!sourceNode || !targetNode) return null;
                        return (
                          <line
                            key={edge.id || `${edge.source}-${edge.target}`}
                            x1={sourceNode.x}
                            y1={sourceNode.y}
                            x2={targetNode.x}
                            y2={targetNode.y}
                            stroke="#0d6efd"
                            strokeWidth={12 / (state?.scale || 1)}
                            strokeDasharray={`${8 / (state?.scale || 1)} ${6 / (state?.scale || 1)}`}
                            opacity="0.9"
                            style={{ pointerEvents: 'none' }}
                          />
                        );
                      })}

                    {Array.isArray(nodes) &&
                      nodes.map((node) => {
                        const isSelectedForEdge = edgeStartNode === node.id;
                        const temLigacao = Array.isArray(edges) ? edges.some((e) => e.source === node.id || e.target === node.id) : false;
                        const isSelectedMulti = isNodeSelected(node.id);

                        if (node.type === 'destination') {
                          const estiloRegra = getEstiloRegraNode(node.id);
                          const origem = isRegraOrigem(node.id);
                          const waypoint = isRegraWaypoint(node.id);

                          let icon = '📍';
                          if (origem && waypoint) icon = '🟣';
                          else if (origem) icon = '🔵';
                          else if (waypoint) icon = '🟢';
                          else if (isSelectedForEdge) icon = '🟢';
                          else if (!temLigacao) icon = '⚠️';

                          const glow = estiloRegra
                            ? estiloRegra.filter
                            : isSelectedForEdge
                            ? 'drop-shadow(0 0 15px #32CD32)'
                            : isSelectedMulti
                            ? 'drop-shadow(0 0 12px #0d6efd)'
                            : !temLigacao && isEditMode
                            ? 'drop-shadow(0 0 5px red)'
                            : 'none';

                          return (
                            <g
                              key={node.id}
                              onClick={(e) => onNodeClick?.(e, node.id)}
                              onMouseDown={(e) => handleNodeMouseDown(e, node.id)}
                              onMouseEnter={(e) => handleNodeHover(e, node, true)}
                              onMouseLeave={(e) => handleNodeHover(e, node, false)}
                              style={{ cursor: 'pointer' }}
                            >
                              <text
                                x={node.x}
                                y={node.y}
                                fontSize={isSelectedForEdge ? 60 : 40 / (state?.scale || 1)}
                                textAnchor="middle"
                                dominantBaseline="middle"
                                style={{ filter: glow }}
                              >
                                {icon}
                              </text>
                            </g>
                          );
                        }
                        if (node.type === 'intersection') {
                          const corBase = getCorCirculacao(node.circulacao);
                          const isExterno = node.circulacao === 'externo';
                          const estiloRegra = getEstiloRegraNode(node.id);

                          return (
                            <g key={node.id}>
                              {isExterno && (
                                <circle
                                  cx={node.x}
                                  cy={node.y}
                                  r={24}
                                  fill="none"
                                  stroke="#ff9800"
                                  strokeWidth={3}
                                  strokeDasharray="6 4"
                                  opacity={0.9}
                                />
                              )}

                              <circle
                                cx={node.x}
                                cy={node.y}
                                r={isSelectedForEdge ? 20 : isRegraOrigem(node.id) || isRegraWaypoint(node.id) ? 20 : isSelectedMulti ? 18 : 14}
                                fill={
                                  estiloRegra?.fill
                                    ? estiloRegra.fill
                                    : isSelectedForEdge
                                    ? '#32CD32'
                                    : corBase
                                }
                                stroke={
                                  estiloRegra?.stroke
                                    ? estiloRegra.stroke
                                    : isSelectedForEdge
                                    ? '#FFFFFF'
                                    : isSelectedMulti
                                    ? '#0d6efd'
                                    : '#333'
                                }
                                strokeWidth={
                                  estiloRegra
                                    ? 5
                                    : isSelectedForEdge
                                    ? 5
                                    : isSelectedMulti
                                    ? 5
                                    : 2
                                }
                                style={{
                                  cursor: 'pointer',
                                  filter:
                                    estiloRegra?.filter
                                      ? estiloRegra.filter
                                      : isSelectedForEdge
                                      ? 'drop-shadow(0 0 8px #32CD32)'
                                      : isSelectedMulti
                                      ? 'drop-shadow(0 0 10px #0d6efd)'
                                      : isExterno
                                      ? 'drop-shadow(0 0 6px rgba(255,152,0,0.8))'
                                      : 'none',
                                }}
                                onClick={(e) => onNodeClick?.(e, node.id)}
                                onMouseDown={(e) => handleNodeMouseDown(e, node.id)}
                                onMouseEnter={(e) => handleNodeHover(e, node, true)}
                                onMouseLeave={(e) => handleNodeHover(e, node, false)}
                              />

                              {modoSelecaoRegra && (isRegraOrigem(node.id) || isRegraWaypoint(node.id)) && (
                                <text
                                  x={node.x}
                                  y={node.y - 26}
                                  fontSize={12}
                                  textAnchor="middle"
                                  fill={isRegraOrigem(node.id) && isRegraWaypoint(node.id) ? '#7c3aed' : isRegraOrigem(node.id) ? '#0d6efd' : '#198754'}
                                  style={{ pointerEvents: 'none', fontWeight: 700 }}
                                >
                                  {isRegraOrigem(node.id) && isRegraWaypoint(node.id)
                                    ? 'ORIGEM / PASSAGEM'
                                    : isRegraOrigem(node.id)
                                    ? 'ORIGEM'
                                    : 'PASSAGEM'}
                                </text>
                              )}
                            </g>
                          );
                        }

                        return null;
                      })}

                    {hoveredNode && (hoveredNode.description || hoveredNode.name) ? (
                      <g style={{ pointerEvents: 'none' }}>
                        <rect
                          x={hoveredNode.x + 10 / (state?.scale || 1)}
                          y={hoveredNode.y - 30 / (state?.scale || 1)}
                          width={String(hoveredNode.description || hoveredNode.name).length * 8 / (state?.scale || 1)}
                          height={22 / (state?.scale || 1)}
                          rx={4 / (state?.scale || 1)}
                          fill="rgba(0,0,0,0.75)"
                        />
                        <text
                          x={hoveredNode.x + 14 / (state?.scale || 1)}
                          y={hoveredNode.y - 14 / (state?.scale || 1)}
                          fill="white"
                          fontSize={14 / (state?.scale || 1)}
                          dominantBaseline="middle"
                        >
                          {hoveredNode.description || hoveredNode.name}
                        </text>
                      </g>
                    ) : null}
                  </g>
                </svg>
              </TransformComponent>
            </>
          )}
        </TransformWrapper>
      </div>
    );
  }
);

export default MapaRoteamento;